import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../utils/cn"

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-lg text-sm font-semibold ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 gap-2 whitespace-nowrap",
  {
    variants: {
      variant: {
        default: "bg-blurple text-white hover:brightness-110",
        rounded:
          "bg-blurple text-white hover:brightness-110 rounded-xl hover:shadow-lg",
        context: "text-white hover:brightness-110",
        utility:
          "bg-grayFlash text-black shadow-md hover:bg-white font-bold border-px box-border",
        destructive: "bg-vividRed text-white hover:brightness-110",
        outline:
          "border-2 border-blurple bg-background text-blurple hover:bg-accent",
        outlineDynamic:
          "border-2 border-current bg-background text-current hover:bg-accent",
        //  If using outlineDynamic, ensure you use style to change color. Example below:
        // style={{
        //   color: coachColor,
        //   borderColor: coachColor,
        // }}
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/50",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-blurple underline-offset-4 hover:underline",
        disable: "bg-grayMist",
        black: "bg-black text-white",
        blurpleGradient:
          "bg-[linear-gradient(to_bottom,_#892FF4,_#2A5FD0)] text-white",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  ariaLabel?: string
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ariaLabel, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        aria-label={ariaLabel}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
